import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    listAdminCategories,
    createAdminCategory,
    updateAdminCategory,
    deleteAdminCategory,
    createAdminSubcategory,
    updateAdminSubcategory,
    deleteAdminSubcategory,
    uploadAdminPhoto,
    deleteAdminPhoto,
} from "@/lib/api";
import { resolvePhotoUrl } from "@/lib/photos";
import {
    Plus,
    Pencil,
    Trash2,
    Loader2,
    Check,
    X,
    ChevronDown,
    ChevronRight,
    ListTree,
    ImagePlus,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_CAT = { id: "", label: "", sort_order: 0, active: true, photo: "" };
const EMPTY_SUB = { id: "", category_id: "", label: "", sort_order: 0, active: true, photo: "" };

const Categories = () => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(EMPTY_CAT);
    const [editId, setEditId] = useState(null);

    const [expandedCat, setExpandedCat] = useState(null);
    const [subForm, setSubForm] = useState(EMPTY_SUB);
    const [subEditId, setSubEditId] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [subPhotoUploading, setSubPhotoUploading] = useState(false);

    const { data: categories = [], isLoading } = useQuery({
        queryKey: ["admin", "categories"],
        queryFn: listAdminCategories,
    });

    const createMutation = useMutation({
        mutationFn: createAdminCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Catégorie créée.");
            setForm(EMPTY_CAT);
        },
        onError: (err) => toast.error(err.response?.data?.detail || "Erreur."),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updateAdminCategory(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Catégorie mise à jour.");
            setEditId(null);
        },
        onError: (err) => toast.error(err.response?.data?.detail || "Erreur."),
    });

    const deleteMutation = useMutation({
        mutationFn: deleteAdminCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Catégorie supprimée.");
        },
        onError: () => toast.error("Impossible de supprimer la catégorie."),
    });

    const subCreateMutation = useMutation({
        mutationFn: createAdminSubcategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Sous-catégorie créée.");
            setSubForm(EMPTY_SUB);
            setSubEditId(null);
        },
        onError: (err) => toast.error(err.response?.data?.detail || "Erreur."),
    });

    const subUpdateMutation = useMutation({
        mutationFn: ({ id, data }) => updateAdminSubcategory(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Sous-catégorie mise à jour.");
            setSubForm(EMPTY_SUB);
            setSubEditId(null);
        },
        onError: (err) => toast.error(err.response?.data?.detail || "Erreur."),
    });

    const subDeleteMutation = useMutation({
        mutationFn: deleteAdminSubcategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Sous-catégorie supprimée.");
        },
        onError: () => toast.error("Impossible de supprimer la sous-catégorie."),
    });

    const handleCreate = (e) => {
        e.preventDefault();
        createMutation.mutate(form);
    };

    const startEdit = (cat) => {
        setEditId(cat.id);
        setForm({ ...cat });
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        updateMutation.mutate({ id: editId, data: form });
    };

    const toggleExpand = (catId) => {
        setExpandedCat((prev) => (prev === catId ? null : catId));
        setSubForm(EMPTY_SUB);
        setSubEditId(null);
    };

    const startSubEdit = (sub) => {
        setSubEditId(sub.id);
        setSubForm({ ...sub });
    };

    const handleSubSubmit = (e) => {
        e.preventDefault();
        const payload = { ...subForm, category_id: expandedCat };
        if (subEditId) {
            subUpdateMutation.mutate({ id: subEditId, data: payload });
        } else {
            subCreateMutation.mutate(payload);
        }
    };

    const handleCatPhotoSelect = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setPhotoUploading(true);
        try {
            const res = await uploadAdminPhoto(file);
            setForm((f) => ({ ...f, photo: res.url }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Échec de l'upload.");
        } finally {
            setPhotoUploading(false);
        }
    };

    const handleCatPhotoRemove = () => {
        const url = form.photo;
        setForm((f) => ({ ...f, photo: "" }));
        if (url && url.startsWith("/uploads/")) {
            deleteAdminPhoto(url.split("/").pop()).catch(() => {});
        }
    };

    const handleSubPhotoSelect = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setSubPhotoUploading(true);
        try {
            const res = await uploadAdminPhoto(file);
            setSubForm((f) => ({ ...f, photo: res.url }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Échec de l'upload.");
        } finally {
            setSubPhotoUploading(false);
        }
    };

    const handleSubPhotoRemove = () => {
        const url = subForm.photo;
        setSubForm((f) => ({ ...f, photo: "" }));
        if (url && url.startsWith("/uploads/")) {
            deleteAdminPhoto(url.split("/").pop()).catch(() => {});
        }
    };

    const sorted = [...categories].sort((a, b) => a.sort_order - b.sort_order);

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight">
                        Catégories
                    </h1>
                    <p className="text-neutral-500 mt-1">
                        Gérez les catégories et leurs sous-catégories du catalogue.
                    </p>
                </div>

                <form
                    onSubmit={editId ? handleUpdate : handleCreate}
                    className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4"
                >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="space-y-1.5">
                            <Label className="text-sm">ID</Label>
                            <Input
                                value={form.id}
                                onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
                                placeholder="coques"
                                disabled={!!editId}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">Label</Label>
                            <Input
                                value={form.label}
                                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                                placeholder="Coques & Protection"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">Ordre</Label>
                            <Input
                                type="number"
                                value={form.sort_order}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))
                                }
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="active"
                                    checked={form.active}
                                    onCheckedChange={(v) =>
                                        setForm((f) => ({ ...f, active: !!v }))
                                    }
                                />
                                <Label htmlFor="active" className="text-sm cursor-pointer">
                                    Active
                                </Label>
                            </div>
                            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                                {editId ? (
                                    <>
                                        <Check className="w-4 h-4 mr-2" />
                                        Mettre à jour
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter
                                    </>
                                )}
                            </Button>
                            {editId && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => {
                                        setEditId(null);
                                        setForm(EMPTY_CAT);
                                    }}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                        <Label className="text-sm shrink-0">Photo</Label>
                        {form.photo ? (
                            <div className="flex items-center gap-3">
                                <img
                                    src={resolvePhotoUrl(form.photo)}
                                    alt=""
                                    className="h-12 w-12 rounded-lg object-cover border border-neutral-200"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleCatPhotoRemove}
                                    disabled={photoUploading}
                                >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                            </div>
                        ) : (
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-neutral-300 px-3 py-2 text-sm text-neutral-600 transition-colors hover:border-[#d4af37]/60 hover:text-neutral-900">
                                {photoUploading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <ImagePlus className="w-4 h-4" />
                                )}
                                {photoUploading ? "Upload en cours…" : "Ajouter une image"}
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    className="hidden"
                                    onChange={handleCatPhotoSelect}
                                    disabled={photoUploading}
                                />
                            </label>
                        )}
                    </div>
                </form>

                <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Ordre</TableHead>
                                <TableHead>ID</TableHead>
                                <TableHead>Label</TableHead>
                                <TableHead>Photo</TableHead>
                                <TableHead>Sous-catégories</TableHead>
                                <TableHead>Active</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#d4af37]" />
                                    </TableCell>
                                </TableRow>
                            ) : sorted.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-neutral-500">
                                        Aucune catégorie.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sorted.map((c) => {
                                    const subs = c.subcategories || [];
                                    const expanded = expandedCat === c.id;
                                    return (
                                        <React.Fragment key={c.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-7 w-7"
                                                        onClick={() => toggleExpand(c.id)}
                                                    >
                                                        {expanded ? (
                                                            <ChevronDown className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>{c.sort_order}</TableCell>
                                                <TableCell className="font-mono text-sm">{c.id}</TableCell>
                                                <TableCell className="font-medium">{c.label}</TableCell>
                                                <TableCell>
                                                    {c.photo ? (
                                                        <img
                                                            src={resolvePhotoUrl(c.photo)}
                                                            alt=""
                                                            className="h-9 w-9 rounded-lg object-cover border border-neutral-200"
                                                        />
                                                    ) : (
                                                        <span className="text-neutral-300">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="inline-flex items-center gap-1.5 text-sm text-neutral-600">
                                                        <ListTree className="w-3.5 h-3.5" />
                                                        {subs.length}
                                                    </span>
                                                </TableCell>
                                                <TableCell>{c.active ? "Oui" : "Non"}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button size="icon" variant="ghost" onClick={() => startEdit(c)}>
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="text-red-600 hover:text-red-700"
                                                            onClick={() => deleteMutation.mutate(c.id)}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                            {expanded && (
                                                <TableRow className="bg-neutral-50/60 hover:bg-neutral-50/60">
                                                    <TableCell colSpan={8} className="p-0">
                                                        <div className="p-5 space-y-4">
                                                            <h3 className="font-display text-sm font-semibold text-neutral-700">
                                                                Sous-catégories de « {c.label} »
                                                            </h3>

                                                            <form
                                                                onSubmit={handleSubSubmit}
                                                                className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end bg-white rounded-lg border border-neutral-200 p-3"
                                                            >
                                                                <div className="space-y-1">
                                                                    <Label className="text-xs">ID</Label>
                                                                    <Input
                                                                        value={subForm.id}
                                                                        onChange={(e) =>
                                                                            setSubForm((f) => ({ ...f, id: e.target.value }))
                                                                        }
                                                                        placeholder="iphone-17-pro-max"
                                                                        disabled={!!subEditId}
                                                                        required
                                                                        className="h-9 text-sm"
                                                                    />
                                                                </div>
                                                                <div className="md:col-span-2 space-y-1">
                                                                    <Label className="text-xs">Label</Label>
                                                                    <Input
                                                                        value={subForm.label}
                                                                        onChange={(e) =>
                                                                            setSubForm((f) => ({ ...f, label: e.target.value }))
                                                                        }
                                                                        placeholder="iPhone 17 Pro Max"
                                                                        required
                                                                        className="h-9 text-sm"
                                                                    />
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <Label className="text-xs">Ordre</Label>
                                                                    <Input
                                                                        type="number"
                                                                        value={subForm.sort_order}
                                                                        onChange={(e) =>
                                                                            setSubForm((f) => ({
                                                                                ...f,
                                                                                sort_order: parseInt(e.target.value, 10) || 0,
                                                                            }))
                                                                        }
                                                                        className="h-9 text-sm"
                                                                    />
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <Checkbox
                                                                        id={`sub-active-${c.id}`}
                                                                        checked={subForm.active}
                                                                        onCheckedChange={(v) =>
                                                                            setSubForm((f) => ({ ...f, active: !!v }))
                                                                        }
                                                                    />
                                                                    <Label htmlFor={`sub-active-${c.id}`} className="text-xs cursor-pointer">
                                                                        Active
                                                                    </Label>
                                                                </div>
                                                                <div className="flex items-center gap-3 md:col-span-5 pt-1">
                                                                    <Label className="text-xs shrink-0">Photo</Label>
                                                                    {subForm.photo ? (
                                                                        <div className="flex items-center gap-3">
                                                                            <img
                                                                                src={resolvePhotoUrl(subForm.photo)}
                                                                                alt=""
                                                                                className="h-10 w-10 rounded-lg object-cover border border-neutral-200"
                                                                            />
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-7 w-7"
                                                                                onClick={handleSubPhotoRemove}
                                                                                disabled={subPhotoUploading}
                                                                            >
                                                                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-xs text-neutral-600 transition-colors hover:border-[#d4af37]/60 hover:text-neutral-900">
                                                                            {subPhotoUploading ? (
                                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                            ) : (
                                                                                <ImagePlus className="w-3.5 h-3.5" />
                                                                            )}
                                                                            {subPhotoUploading ? "Upload…" : "Ajouter une image"}
                                                                            <input
                                                                                type="file"
                                                                                accept="image/jpeg,image/png,image/webp,image/gif"
                                                                                className="hidden"
                                                                                onChange={handleSubPhotoSelect}
                                                                                disabled={subPhotoUploading}
                                                                            />
                                                                        </label>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2 md:col-span-5 justify-end">
                                                                    <Button
                                                                        type="submit"
                                                                        size="sm"
                                                                        disabled={subCreateMutation.isPending || subUpdateMutation.isPending}
                                                                    >
                                                                        {subEditId ? (
                                                                            <>
                                                                                <Check className="w-3.5 h-3.5 mr-1.5" />
                                                                                Mettre à jour
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Plus className="w-3.5 h-3.5 mr-1.5" />
                                                                                Ajouter
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                    {subEditId && (
                                                                        <Button
                                                                            type="button"
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            onClick={() => {
                                                                                setSubEditId(null);
                                                                                setSubForm(EMPTY_SUB);
                                                                            }}
                                                                        >
                                                                            <X className="w-3.5 h-3.5" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </form>

                                                            {subs.length > 0 ? (
                                                                <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
                                                                    <Table>
                                                                        <TableHeader>
                                                                            <TableRow>
                                                                                <TableHead className="text-xs">Ordre</TableHead>
                                                                                <TableHead className="text-xs">ID</TableHead>
                                                                                <TableHead className="text-xs">Label</TableHead>
                                                                                <TableHead className="text-xs">Active</TableHead>
                                                                                <TableHead className="text-xs text-right">Actions</TableHead>
                                                                            </TableRow>
                                                                        </TableHeader>
                                                                        <TableBody>
                                                                            {[...subs]
                                                                                .sort((a, b) => a.sort_order - b.sort_order)
                                                                                .map((s) => (
                                                                                    <TableRow key={s.id}>
                                                                                        <TableCell className="text-sm">{s.sort_order}</TableCell>
                                                                                        <TableCell className="font-mono text-xs">{s.id}</TableCell>
                                                                                        <TableCell className="text-sm">{s.label}</TableCell>
                                                                                        <TableCell className="text-sm">{s.active ? "Oui" : "Non"}</TableCell>
                                                                                        <TableCell className="text-right">
                                                                                            <div className="flex items-center justify-end gap-1">
                                                                                                <Button
                                                                                                    size="icon"
                                                                                                    variant="ghost"
                                                                                                    className="h-7 w-7"
                                                                                                    onClick={() => startSubEdit(s)}
                                                                                                >
                                                                                                    <Pencil className="w-3.5 h-3.5" />
                                                                                                </Button>
                                                                                                <Button
                                                                                                    size="icon"
                                                                                                    variant="ghost"
                                                                                                    className="h-7 w-7 text-red-600 hover:text-red-700"
                                                                                                    onClick={() => subDeleteMutation.mutate(s.id)}
                                                                                                >
                                                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                                                </Button>
                                                                                            </div>
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </div>
                                                            ) : (
                                                                <p className="text-sm text-neutral-500">
                                                                    Aucune sous-catégorie pour « {c.label} ».
                                                                </p>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </AdminLayout>
    );
};

export default Categories;
